import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axiosInstance from "../../../interceptors";
import { baseUrl } from "../../../config/config";
import NotesFeed from "./NotesFeed";
import FeedbackTab from "./FeedbackTab";
import RejectionModal from "./RejectionModal";
import RecommendationBadge from "../../../components/shared/RecommendationBadge";
import ScoreBreakdown from "./ScoreBreakdown";
import ScorecardsTab from "./ScorecardsTab";
import CodingTab from "./CodingTab";
import {
  fetchPercentile,
  selectPercentile,
} from "../../../redux/slices/percentile-slice";

// Coding tab is conditionally rendered when the report response includes
// at least one codingSubmissions row — most candidates won't have any.
const TABS = [
  { key: "overview", label: "Overview" },
  { key: "competency", label: "Competency" },
  { key: "coding", label: "Coding", showWhen: (r) => Array.isArray(r?.codingSubmissions) && r.codingSubmissions.length > 0 },
  { key: "scorecards", label: "Scorecards" },
  { key: "integrity", label: "Integrity" },
  { key: "personality", label: "Personality" },
  { key: "references", label: "References" },
  { key: "hr", label: "HR feedback" },
  { key: "feedback", label: "Interviewer feedback" },
];

const fmt = (v, digits = 1) => (v == null ? "—" : (+v).toFixed(digits));
const pct = (v) => (v == null ? "—" : `${Math.round(+v)}%`);

const scoreColor = (v) => {
  if (v == null) return "text-slate-400";
  const n = +v;
  const t10 = n <= 10;
  if (t10 ? n >= 8 : n >= 80) return "text-emerald-600 font-semibold";
  if (t10 ? n >= 6 : n >= 60) return "text-amber-600";
  return "text-red-600";
};

const riskColor = (v) => {
  if (!v) return "bg-slate-100 text-slate-600";
  const u = v.toUpperCase();
  if (u === "HIGH") return "bg-red-100 text-red-700";
  if (u === "MODERATE" || u === "MEDIUM") return "bg-amber-100 text-amber-700";
  return "bg-emerald-100 text-emerald-700";
};

export default function CandidateReport() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const dispatch = useDispatch();
  const candidateId = params.get("candidateId");
  const interviewId = params.get("interviewId");
  const [tab, setTab] = useState("overview");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejected, setRejected] = useState(false);
  const [breakdownOpen, setBreakdownOpen] = useState(false);

  const overallPercentile = useSelector(
    selectPercentile({ userId: candidateId, interviewId, dimension: "overall" }),
  );

  useEffect(() => { document.title = "Candidate report - Ample Hire"; }, []);

  useEffect(() => {
    if (!candidateId) return;
    setLoading(true);
    setError(null);
    const url = `${baseUrl}/job-posting/api/candidate-report/${candidateId}/full` +
      (interviewId ? `?interviewId=${interviewId}` : "");
    axiosInstance
      .get(url)
      .then((r) => setReport(r.data))
      .catch((e) => setError(e?.message || "Failed to load report"))
      .finally(() => setLoading(false));
  }, [candidateId, interviewId]);

  useEffect(() => {
    if (!candidateId) return;
    dispatch(fetchPercentile({ userId: candidateId, interviewId, dimension: "overall" }));
  }, [dispatch, candidateId, interviewId]);

  const downloadPdf = () => {
    window.open(`${baseUrl}/job-posting/candidate/${candidateId}/report/pdf`, "_blank");
  };

  return (
    <div className="page-enter">
      <div className="flex items-end justify-between mb-4">
        <div>
          <button
            onClick={() => nav("/admin/interviews")}
            className="text-xs text-vf-muted hover:text-vf-base mb-1"
          >
            ← Back to sessions
          </button>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold text-vf-base">
              {report?.candidateName || "Candidate report"}
            </h1>
            {report?.recommendation?.level && (
              <RecommendationBadge
                level={report.recommendation.level}
                confidence={report.recommendation.confidence}
                reasoning={report.recommendation.reasoning}
                size="md"
                showConfidence
              />
            )}
            {report?.recommendation?.factors?.length > 0 && (
              <button
                onClick={() => setBreakdownOpen(true)}
                aria-label="View score breakdown"
                title="How this score was computed"
                className="w-5 h-5 rounded-full border border-slate-300 text-slate-500 hover:text-vf-primary hover:border-vf-primary text-xs leading-none flex items-center justify-center"
              >
                ?
              </button>
            )}
          </div>
          <p className="text-sm text-vf-muted mt-1">
            {report?.position || ""}
            {report?.position && report?.email ? " · " : ""}
            {report?.email || ""}
          </p>
          {overallPercentile?.interpretationText && (
            <p className="text-xs text-vf-muted mt-0.5 font-medium">
              {overallPercentile.interpretationText}
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => nav(`/admin/compare?ids=${candidateId}`)}
            className="px-3 py-1.5 rounded border border-slate-300 text-xs"
          >
            Compare
          </button>
          <button
            onClick={downloadPdf}
            className="px-3 py-1.5 rounded border border-slate-300 text-xs"
          >
            Download PDF
          </button>
          <button
            onClick={() => axiosInstance.post(`${baseUrl}/job-posting/candidate/${candidateId}/shortlist`)}
            className="px-3 py-1.5 rounded bg-vf-primary text-white text-xs"
          >
            Shortlist
          </button>
          <button
            onClick={() => setRejectOpen(true)}
            disabled={rejected}
            className="px-3 py-1.5 rounded border border-red-300 text-red-700 text-xs hover:bg-red-50 disabled:opacity-50"
          >
            {rejected ? "Rejected" : "Reject"}
          </button>
        </div>
      </div>

      {rejectOpen && (
        <RejectionModal
          candidateId={candidateId}
          jobId={report?.jobId || report?.overview?.assessment?.jobId}
          candidateName={report?.candidateName}
          candidateEmail={report?.email}
          onClose={() => setRejectOpen(false)}
          onDone={() => { setRejectOpen(false); setRejected(true); }}
        />
      )}

      <div className="bg-white border border-slate-200 rounded-xl">
        <div
          role="tablist"
          aria-label="Candidate report sections"
          className="flex border-b border-slate-200 px-2 overflow-x-auto scroll-smooth"
        >
          {TABS.filter((t) => !t.showWhen || t.showWhen(report)).map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              aria-current={tab === t.key ? "page" : undefined}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap min-h-[44px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-vf-primary ${tab === t.key ? "border-b-2 border-vf-primary text-vf-primary" : "text-vf-muted hover:text-vf-base"}`}
            >
              {t.label}
              {t.key === "coding" && report?.codingSubmissions?.length > 0 && (
                <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-vf-tint/40 text-vf-base">{report.codingSubmissions.length}</span>
              )}
            </button>
          ))}
        </div>
        <div className="p-6 min-h-[400px]">
          {!candidateId ? (
            <Empty>Open this page from a candidate row in Sessions.</Empty>
          ) : loading ? (
            <Empty>Loading report…</Empty>
          ) : error ? (
            <Empty tone="error">{error}</Empty>
          ) : !report ? (
            <Empty>No report data.</Empty>
          ) : (
            <>
              {tab === "overview"    && <OverviewTab data={report.overview} recommendation={report.recommendation} competency={report.competency} />}
              {tab === "competency"  && <CompetencyTab rows={report.competency} candidateId={candidateId} interviewId={report.interviewId} />}
              {tab === "coding"      && <CodingTab submissions={report.codingSubmissions || []} />}
              {tab === "scorecards"  && <ScorecardsTab candidateId={candidateId} jobId={report?.jobId || report?.overview?.assessment?.jobId} />}
              {tab === "integrity"   && <IntegrityTab data={report.integrity} />}
              {tab === "personality" && <PersonalityTab data={report.personality} />}
              {tab === "references"  && <ReferencesTab rows={report.references} />}
              {tab === "hr"          && <HrTab data={report.hr} />}
              {tab === "feedback"    && <FeedbackTab candidateId={candidateId} jobId={report?.jobId || report?.overview?.assessment?.jobId} candidateName={report?.candidateName} />}
            </>
          )}
        </div>
      </div>

      {candidateId && (
        <NotesFeed candidateId={candidateId} jobId={report?.jobId || report?.overview?.assessment?.jobId} />
      )}

      {breakdownOpen && (
        <div
          className="fixed inset-0 bg-slate-900/40 z-[1000] flex items-center justify-center p-4"
          onClick={() => setBreakdownOpen(false)}
        >
          <div
            className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
              <div className="text-sm font-semibold text-vf-base">Score breakdown</div>
              <button
                onClick={() => setBreakdownOpen(false)}
                className="w-6 h-6 rounded text-slate-500 hover:bg-slate-100"
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="p-4">
              <ScoreBreakdown
                recommendation={report?.recommendation}
                competency={report?.competency}
                overallScore={report?.overview?.assessment?.overallScore}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Tab renderers ──────────────────────────────────────────────────────────

const OverviewTab = ({ data, recommendation, competency }) => {
  if (!data) return <Empty>No overview data.</Empty>;
  const a = data.assessment || {};
  const au = data.audio || {};
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Overall score"      value={fmt(a.overallScore, 1)}    tone={scoreColor(a.overallScore)} />
        <Stat label="Status"             value={a.assessmentStatus || "—"} />
        <Stat label="Scored / total"     value={`${a.scoredQuestions ?? "—"} / ${a.totalQuestions ?? "—"}`} />
        <Stat label="Audio score"        value={fmt(au.audioScore, 1)}     tone={scoreColor(au.audioScore)} />
      </div>
      <ScoreBreakdown
        recommendation={recommendation}
        competency={competency}
        overallScore={a.overallScore}
      />
      {a.comment && (
        <Section title="AI comment">
          <p className="text-sm text-vf-base leading-relaxed">{a.comment}</p>
        </Section>
      )}
      {data.aiSummary && (
        <Section title="AI summary">
          <p className="text-sm text-vf-base leading-relaxed whitespace-pre-line">{data.aiSummary}</p>
        </Section>
      )}
      <Section title="Audio metrics">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Voice pitch (Hz)"    value={fmt(au.voicePitch, 0)} />
          <Stat label="Fluency"             value={fmt(au.speechFluency, 2)} />
          <Stat label="Speed (wpm)"         value={fmt(au.speakingSpeed, 0)} />
          <Stat label="Pauses / 15s"        value={fmt(au.pausesPer15Sec, 1)} />
        </div>
      </Section>
    </div>
  );
};

const CompetencyTab = ({ rows, candidateId, interviewId }) => {
  const dispatch = useDispatch();
  // Lazy per-skill percentile fetch on tab open. Deduped by competency name.
  const uniqueCompetencies = useMemo(
    () => Array.from(new Set((rows || []).map((r) => r.competency).filter(Boolean))),
    [rows],
  );
  useEffect(() => {
    if (!candidateId) return;
    uniqueCompetencies.forEach((c) => {
      dispatch(fetchPercentile({ userId: candidateId, interviewId, dimension: c }));
    });
  }, [dispatch, candidateId, interviewId, uniqueCompetencies]);

  if (!rows || rows.length === 0) return <Empty>No competency data.</Empty>;
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-slate-200 text-xs text-vf-muted uppercase">
          <th className="text-left p-2">Competency</th>
          <th className="text-left p-2">Type</th>
          <th className="text-left p-2">Score</th>
          <th className="text-left p-2">Rank</th>
          <th className="text-left p-2">AI comment</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className="border-b border-slate-100 align-top">
            <td className="p-2 text-vf-base font-medium">{r.competency || "—"}</td>
            <td className="p-2 text-vf-muted text-xs">{r.skillType || "—"}</td>
            <td className={`p-2 ${scoreColor(r.score)}`}>{fmt(r.score, 1)}</td>
            <td className="p-2">
              <PercentileCell
                userId={candidateId}
                interviewId={interviewId}
                dimension={r.competency}
              />
            </td>
            <td className="p-2 text-vf-muted text-xs leading-relaxed">{r.aiComment || "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const PercentileCell = ({ userId, interviewId, dimension }) => {
  const data = useSelector(selectPercentile({ userId, interviewId, dimension }));
  if (!data) return <span className="text-vf-muted text-xs">…</span>;
  if (data.percentile == null) return <span className="text-vf-muted text-xs">—</span>;
  const topPct = Math.round(100 - Number(data.percentile));
  return (
    <span
      className="text-xs font-medium text-vf-primary"
      title={`Rank ${data.rank} of ${data.totalCandidates}`}
    >
      Top {topPct}%
    </span>
  );
};

const IntegrityTab = ({ data }) => {
  if (!data || Object.keys(data).length === 0) return <Empty>No integrity data.</Empty>;
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Stat label="Integrity score" value={fmt(data.integrityScore, 1)} tone={scoreColor(data.integrityScore)} />
        <div>
          <div className="text-xs text-vf-muted mb-1">Risk level</div>
          <span className={`px-2 py-1 rounded text-xs font-semibold ${riskColor(data.riskLevel)}`}>
            {data.riskLevel || "—"}
          </span>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Stat label="Tab switches"            value={data.tabSwitchCount ?? "—"} />
        <Stat label="Multiple faces"          value={data.multipleFacesCount ?? "—"} />
        <Stat label="Gaze off screen"         value={data.gazeOffScreenCount ?? "—"} />
        <Stat label="Face-not-detected (s)"   value={data.faceNotDetectedSeconds ?? "—"} />
        <Stat label="Audio silence (s)"       value={data.audioSilenceSeconds ?? "—"} />
        <Stat label="Copy/paste events"       value={data.copyPasteEvents ?? "—"} />
      </div>
    </div>
  );
};

const PersonalityTab = ({ data }) => {
  const traits = data?.traits || [];
  if (traits.length === 0) return <Empty>No personality data.</Empty>;
  return (
    <div className="space-y-2">
      {traits.map((t, i) => (
        <div key={i} className="flex items-center justify-between border-b border-slate-100 py-2">
          <div>
            <div className="text-sm font-medium text-vf-base">{t.trait}</div>
            {t.aiComment && <div className="text-xs text-vf-muted mt-0.5">{t.aiComment}</div>}
          </div>
          <div className={`text-sm ${scoreColor(t.score)}`}>{fmt(t.score, 1)}</div>
        </div>
      ))}
    </div>
  );
};

const ReferencesTab = ({ rows }) => {
  if (!rows || rows.length === 0) return <Empty>No references recorded.</Empty>;
  return <pre className="text-xs text-vf-muted">{JSON.stringify(rows, null, 2)}</pre>;
};

const STAGE_LABELS = {
  filterReject: "Filter",
  recruiterRoundStatus: "Recruiter round",
  l1RoundStatus: "L1 round",
  l1Status: "L1 decision",
  l2Status: "L2 decision",
  l3Status: "L3 decision",
  otherStatus: "Other",
};

const HrTab = ({ data }) => {
  const stages = data?.stages || [];
  if (stages.length === 0) return <Empty>No HR pipeline history.</Empty>;
  return (
    <div className="space-y-5">
      {stages.map((s, i) => (
        <div key={i} className="border border-slate-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-semibold text-vf-base">Job #{s.jobId}</div>
            {s.isPublished && (
              <span className="px-2 py-0.5 rounded text-xs bg-emerald-50 text-emerald-700 font-medium">Published</span>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
            {Object.entries(STAGE_LABELS).map(([k, label]) => (
              <div key={k} className="text-xs">
                <span className="text-vf-muted">{label}:</span>{" "}
                <span className="text-vf-base font-medium">{s[k] ?? 0}</span>
              </div>
            ))}
          </div>
          {[s.recruiterNote, s.l1Note, s.l2Note, s.l3Note].filter(Boolean).length > 0 && (
            <div className="space-y-1 text-xs text-vf-muted border-t border-slate-100 pt-2">
              {s.recruiterNote && <div><b>Recruiter:</b> {s.recruiterNote}</div>}
              {s.l1Note && <div><b>L1:</b> {s.l1Note}</div>}
              {s.l2Note && <div><b>L2:</b> {s.l2Note}</div>}
              {s.l3Note && <div><b>L3:</b> {s.l3Note}</div>}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// ── Shared primitives ──────────────────────────────────────────────────────

const Stat = ({ label, value, tone }) => (
  <div className="border border-slate-200 rounded-lg p-3 bg-white">
    <div className="text-xs text-vf-muted">{label}</div>
    <div className={`text-lg font-semibold mt-0.5 ${tone || "text-vf-base"}`}>{value}</div>
  </div>
);

const Section = ({ title, children }) => (
  <div>
    <div className="text-xs text-vf-muted uppercase tracking-wide font-semibold mb-2">{title}</div>
    {children}
  </div>
);

const Empty = ({ children, tone }) => (
  <div className={`text-center text-sm py-12 ${tone === "error" ? "text-red-600" : "text-vf-muted"}`}>
    {children}
  </div>
);
